/* Copyright (c) 2018 Backpack Health LLC */

import * as React from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { AppHeaderScrollableGroup } from '../../../../components/appNav/AppHeaderScrollable';
import ContentFrame from '../../../../applicationFrame/ContentFrame';
import { ScrollAwareContext, ScrollAwareScroller, ScrollAwareAppNavStep } from '../../../../components/ScrollAware';
import { getProfiles } from '../../../redux/reduxSelectors';
import { UserIntentType } from '../../../../../models/UserModel';
import { actions as groupActions, selectors as groupSelectors } from '../../../../redux/ducks/group';

import GroupMetadata from '../../../../../models/GroupMetadata';
import ProfileModel from '../../../../../models/ProfileModel';
import NavigationActions from '../../../redux/NavigationActions';
import RouteNames from '../../../RouteNames';
import ProfileActions from '../../../redux/ProfileActions';
import { withRouter, RouteComponentProps, Redirect } from 'react-router';
import { actions as userActions } from '../../../../redux/ducks/user';
import { userSelectors } from '../../../../redux/selectors_authorizedApp';
import { TrackEvent } from '../../../../../utilities/UserMetrics';

import { Buttons, Card, Text } from '../../../../../bphComponents/bundle';
import ContentWrapper from '../../../../components/ContentWrapper';
import { Cell, Grid } from '../../../../components/Grid';
import ProfileAvatar from '../../../../components/ProfileAvatar';
import IconAdd from '../../../../components/icons/IconAdd';

interface Props extends RouteComponentProps<{}> {
  userData: any;
  profiles: ProfileModel[];
  members: { [profileId: number]: boolean };
  groupId: number;
  group: any;
  requestGroup(groupId): void;
  requestProfileGroups(profileId): void;
  setActiveGroup(groupId): void;
  clearIntent(userData): void;
  switchToProfile(profileData): void;
}

interface State {
  selectedProfileId: number;
}

@(withRouter as any)
@connect(
  (state) => {
    const userData = userSelectors.getUser(state).data;
    const { intent } = userData;
    const groupId = intent && intent.type === UserIntentType.join_group && intent.join_group && intent.join_group.group_id;
    const group = groupSelectors.getGroup(state, groupId);
    const profiles = getProfiles(state);

    const members = profiles.reduce(
      (r, profile) => {
        r[profile.data.id] = groupSelectors.isMember(state, profile.data.id, groupId);
        return r;
      },
      {},
    );

    profiles.sort((p1, p2) => {
      if (!p1.isDependent()) return -1;
      if (!p2.isDependent()) return 1;

      // + to force TS to convert dates to numbers
      return +new Date(p1.data.date_of_birth) - +new Date(p2.data.date_of_birth);
    });

    return ({
      group,
      groupId,
      profiles,
      userData,
      members,
    });
  },
  (dispatch) => ({
    requestGroup: (groupId) => {
      dispatch(groupActions.requestGroupById(groupId));
    },
    switchToProfile: (profileData) => {
      dispatch(ProfileActions.switchToProfile(profileData));
    },
    requestProfileGroups: (profileId) => {
      dispatch(groupActions.requestGroups(profileId));
    },
    setActiveGroup: (groupId) => {
      dispatch(groupActions.setActiveGroup(groupId));
    },
    clearIntent: (userData) => {
      dispatch(userActions.submitUserUpdate(
        {
          ...userData,
          intent: null,
        },
        () => {},
      ));
    },
  }),
)
export class IntendedGroup extends React.Component<Props, State> {
  state: State = {
    selectedProfileId: null,
  };

  render() {
    const { groupId, group, profiles, members } = this.props;
    const { selectedProfileId } = this.state;

    const avatarSize = 48;
    const addIconSize = 32;

    if (!groupId) {
      return <Redirect to={RouteNames.myGroups()}/>;
    }

    return (
      <div>
        <ScrollAwareContext>
          <ScrollAwareAppNavStep
            colorThemeName="group"
            cancel={this.cancel}
            submit={this.join}
            submitText="joinGroup.join.button.label"
            submitDisabled={!selectedProfileId}
            color="white"
          />

          <ContentFrame>
            <ScrollAwareScroller>

              <AppHeaderScrollableGroup
                group={group}
                groupId={groupId}
              />

              <ContentWrapper>
                <Card.ExternalContent noMarginTop>
                  <Text.TextHeadline className="font-color-gsDark">
                    <FormattedMessage id="joinGroup.heading.text"/>
                  </Text.TextHeadline>
                  <Text.TextFootnote className="">
                    <FormattedMessage id="joinGroup.intro.text"/>
                  </Text.TextFootnote>
                </Card.ExternalContent>

                <Card.CardShape>
                  <ul className="util-list-top-border">

                  { profiles.map((profile) => (
                      <Card.SelectableCardContentListItem
                        key={profile.data.id}
                        hasBottomBorder
                        isSelected={members[profile.data.id] || profile.data.id === selectedProfileId}
                        isHighlighted={false}
                        onClick={() => this.setState({ selectedProfileId: profile.data.id })}
                        isDisabled={members[profile.data.id]}
                        selectedClassName={members[profile.data.id] ? 'fill-color-gsMiddle' : 'theme-group base fill'}
                        highlightedClassName="theme-group base trans-10 background"
                      >
                        <Grid alignItems="center">
                          <Cell>
                            <ProfileAvatar size={avatarSize} fontSize={15} profile={profile}/>
                          </Cell>
                          <Cell style={{ marginLeft: 14 }}>
                            <Text.TextHeadline>
                              {profile.isDependent() ? profile.greetingName() : <FormattedMessage id="profileSwitcherSelector.me.avatar.label"/>}
                            </Text.TextHeadline>
                          </Cell>
                        </Grid>
                      </Card.SelectableCardContentListItem>
                  ))}
                  <Card.CardContentListItem onClick={this.createProfile}>
                    <Grid alignItems="center">
                      <Cell style={{ paddingLeft: (avatarSize - addIconSize) / 2, paddingRight: (avatarSize - addIconSize) / 2 }}>
                        <IconAdd fillClassName="fill-color-transparent" strokeClassName="theme-group base stroke" size={'' + addIconSize} />
                      </Cell>
                      <Cell style={{ marginLeft: 14 }}>
                        <div className="font-subhead theme-group base color">
                          <FormattedMessage id="joinGroup.addProfile.button.label"/>
                        </div>
                      </Cell>
                    </Grid>
                  </Card.CardContentListItem>
                  </ul>
                </Card.CardShape>
              </ContentWrapper>


            </ScrollAwareScroller>

          </ContentFrame>
        </ScrollAwareContext>
      </div>
    );
  }

  UNSAFE_componentWillMount() {
    const { setActiveGroup, requestGroup, requestProfileGroups, groupId, profiles } = this.props;

    setActiveGroup(groupId);
    requestGroup(groupId);

    profiles.forEach(profile => requestProfileGroups(profile.data.id));
  }

  cancel = () => {
    const { history, clearIntent, userData } = this.props;

    clearIntent(userData);
    NavigationActions.goTo(history, RouteNames.myGroups());
  }

  join = () => {
    const { group, groupId, history, clearIntent, userData, profiles, switchToProfile } = this.props;
    const { selectedProfileId } = this.state; // pass to join group

    clearIntent(userData);

    const groupMetadata = new GroupMetadata(group);
    TrackEvent.groups.externalJoinConfirmed(group.name);

    const profile = profiles.find(p => p.data.id === selectedProfileId);
    switchToProfile(profile.data);

    NavigationActions.goTo(history, RouteNames.joinGroup(`${groupId}`, selectedProfileId));
  }

  createProfile = () => {
    const { history } = this.props;

    ProfileActions.requestNextProfileColor();
    NavigationActions.goTo(history, RouteNames.createDependentProfile());
  }
}
